import React from "react";
import ReactDOM from "react-dom";
import { commitMutation, fetchQuery, graphql, QueryRenderer } from "react-relay";

import type { JiraIntegrationLinkProps } from ".";
import cx from "classnames";
import { debounce, memoize } from "lodash";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, NodeSelection, TextSelection, Transaction } from "prosemirror-state";
import { Search, type SearchResultProps } from "semantic-ui-react";
import uuid4 from "uuid/v4";

import { nodeAttrsToStatus } from "~/actionItems/utils";
import type { AttendeeAttrs } from "~/common/Attendees";
import { ReactComponent as JiraLogo } from "~/legacy-assets/images/logos/jira.svg";
import type { MutableProseNodeAttrs } from "~/lib/prose";
import { setBlockType } from "~/lib/prose/commands";
import { getContext } from "~/lib/prose/plugins/context";
import { setTooltip } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import toasty from "~/ui/toast";

import type { IntegrationLinkCreateIssueMutation as CreateIssueMutation } from "~/__generated__/IntegrationLinkCreateIssueMutation.graphql";
import type { IntegrationLinkFindExistingIssueQuery as FindExistingIssueQuery } from "~/__generated__/IntegrationLinkFindExistingIssueQuery.graphql";
import type { IntegrationLinkGetIssueQuery as GetIssueQuery } from "~/__generated__/IntegrationLinkGetIssueQuery.graphql";
import type { IntegrationLinkJiraProjectQuery as JiraProjectQuery } from "~/__generated__/IntegrationLinkJiraProjectQuery.graphql";
import type { IntegrationLinkJiraProjectsQuery as JiraProjectsQuery } from "~/__generated__/IntegrationLinkJiraProjectsQuery.graphql";

import styles from "./css/IntegrationLink.module.css";

type Overwrite<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U;

const createIssueMutation = graphql`
	mutation IntegrationLinkCreateIssueMutation($input: CreateJiraIssueInput!) {
		createJiraIssue(input: $input) {
			ok
			id
			key

			status {
				name
				statusCategory {
					colorName
				}
			}
		}
	}
`;

const jiraProjectsQuery = graphql`
	query IntegrationLinkJiraProjectsQuery {
		viewer {
			jiraProjects {
				id
				name
				key
			}
		}
	}
`;

const jiraProjectQuery = graphql`
	query IntegrationLinkJiraProjectQuery($projectId: String!) {
		viewer {
			jiraProject(projectId: $projectId) {
				id
				name
				key
				pushStatus
				twoWaySync
				jiraIssueTypes {
					name
					iconUrl
					id
					externalId
				}
			}
		}
	}
`;

const findExistingIssueQuery = graphql`
	query IntegrationLinkFindExistingIssueQuery($query: String!) {
		viewer {
			jiraSearch(text: $query) {
				id
				key
				img
				summary
				externalId
			}
		}
	}
`;

const getIssueQuery = graphql`
	query IntegrationLinkGetIssueQuery($id: ID) {
		viewer {
			jiraIssue(issueId: $id) {
				key
				summary
				dueDate
				externalId
				externalProjectId

				status {
					name
					statusCategory {
						colorName
					}
				}

				assignee {
					id
				}

				project {
					id
					pushStatus
					twoWaySync

					avatarUrls {
						avatar24
					}
				}
			}
		}
	}
`;

type JiraIssueSearchResultType = NonNullable<
	NonNullable<FindExistingIssueQuery["response"]["viewer"]["jiraSearch"]>[0]
>;
type JiraIssueType = NonNullable<GetIssueQuery["response"]["viewer"]["jiraIssue"]>;

interface IntegrationLinkModalProps {
	node: ProseMirrorNode;
	selection: NodeSelection;
	dispatch: (tr: Transaction) => void;
	editorState: EditorState;
	cancel: () => void;
	initialText: string;
	isAccountAdmin?: boolean;
}

interface IntegrationLinkModalState {
	loading: boolean;
	projectId: string;
	issueType: string;
	selectedTab: string;

	// For Creating an issue
	title: string;

	inputError: string;
	// The actual text in the searchbox that to find an issue that the user is editing
	search: string;
	// Debounced version of searchbox text that is actually sent to the server
	searchDebounced: string;
	selectedIssue?: JiraIssueType;
	selectedIssueSearchResult?: JiraIssueSearchResultType;
}

class IntegrationLinkModal extends React.Component<IntegrationLinkModalProps, IntegrationLinkModalState> {
	constructor(props: IntegrationLinkModalProps) {
		super(props);
		this.state = {
			loading: false,
			selectedTab: this.props.initialText.length > 0 ? "create" : "link",
			projectId: "",
			issueType: "",

			inputError: "",
			title: this.props.initialText,
			search: "",
			searchDebounced: "",
		};
	}

	onClickTab = memoize((selectedTab: string) => (e: React.MouseEvent) => {
		e.preventDefault();
		this.setState({ selectedTab });
	});

	createIssue = () => {
		const { projectId, issueType, title, loading } = this.state;

		if (loading) {
			return;
		}

		if (title.length > 255) {
			this.setState({ inputError: "Jira's issue title is limited to 255 characters." });
			return;
		}

		this.setState({ loading: true });

		const { node, dispatch, editorState, selection, cancel } = this.props;
		const { noteId, jira } = getContext(editorState);

		function nodeToCreateIssueStatus(node: ProseMirrorNode) {
			const attrs = node.attrs;

			if (attrs.done) {
				return "DONE";
			}
			if (attrs.wontDo) {
				return "WONT_DO";
			}
			return "OPEN";
		}

		commitMutation<CreateIssueMutation>(environment, {
			mutation: createIssueMutation,
			variables: {
				input: {
					project: projectId,
					issueType: issueType,
					status: nodeToCreateIssueStatus(node),
					dueDate: node.attrs.dueDate,
					title: title,
					noteId: noteId,
					assigneeId: node.attrs.assignees.length > 0 ? node.attrs.assignees[0].id : null,
				},
			},
			onCompleted: (response, errors) => {
				if (!errors && response && response.createJiraIssue) {
					const linkedIssue = {
						jiraConnectionID: jira.id,
						provider: "JIRA",
						id: response.createJiraIssue.id,
						key: response.createJiraIssue.key,
						project: projectId,
						statusName: response.createJiraIssue.status.name,
						statusColor: response.createJiraIssue.status.statusCategory.colorName,
					};

					if (node.type.name !== "todo") {
						setBlockType(editorState.schema.nodes["todo"], {
							linkedIssue,
						})(editorState, (tr: Transaction) => {
							const mapping = tr.mapping;
							const startM = mapping.map(selection.$from.pos, 1);
							const endM = mapping.map(selection.$to.pos, 1);
							const modifiedTr = tr.replaceWith(startM + 1, endM, editorState.schema.text(title));
							dispatch(modifiedTr);
							cancel();
						});
					} else {
						let tr = editorState.tr.setNodeMarkup(selection.$from.pos, node.type, {
							...node.attrs,
							linkedIssue,
						});

						tr.replaceRangeWith(selection.$from.pos + 1, selection.$to.pos - 1, editorState.schema.text(title));

						dispatch(tr);
						cancel();
					}
					toasty.success("This action item has been successfully linked with Jira.");
				} else {
					toasty.error("Sorry, something went wrong and we couldn't create the issue.");
				}

				this.setState({ loading: false });
			},
		});
	};

	titleUpdated = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ title: event.target.value || "" });
	};

	onProjectChange = (data: SingleValue<SelectorOption>) => {
		if (data) this.setState({ projectId: data.value });
	};

	onIssueTypeChange = (data: SingleValue<SelectorOption>) => {
		if (data) this.setState({ issueType: data.value });
	};

	renderCreateNewIssue = () => {
		return (
			<div className={styles.grid}>
				<div className={styles.inputTitleGrid}>
					<div className={styles.label}>Title:</div>
					<Input
						name="jira_issue_title"
						autoFocus
						type="text"
						value={this.state.title}
						onChange={this.titleUpdated}
						className={cx(styles.inputTitle, this.state.inputError ? styles.inputTitleWarning : undefined)}
						placeholder="Set a title for this issue"
					/>
					{this.state.inputError ? <div className={styles.inputWarning}>{this.state.inputError}</div> : undefined}
				</div>
				<div className={styles.label}>Project:</div>
				<QueryRenderer<JiraProjectsQuery>
					environment={environment}
					query={jiraProjectsQuery}
					variables={{}}
					// eslint-disable-next-line react/jsx-no-bind
					render={({ props }) => {
						const filteredOptions = (props?.viewer.jiraProjects || []).filter(project => project !== null);

						const options = filteredOptions.map(project => ({
							label: `[${project.key}] ${project.name}`,
							value: project.id,
						}));

						const currentProject = filteredOptions.find(o => o.id === this.state.projectId);

						let label = "";

						if (currentProject) label = `[${currentProject.key}] ${currentProject.name}`;

						return (
							<Selector
								isSearchable
								isDisabled={!Boolean(props?.viewer.jiraProjects)}
								placeholder="Select a project"
								value={
									this.state.projectId
										? {
												value: this.state.projectId,
												label: label,
											}
										: undefined
								}
								options={options}
								onChange={this.onProjectChange}
							/>
						);
					}}
				/>
				<QueryRenderer<JiraProjectQuery>
					environment={environment}
					query={(this.state.projectId ? true : null) && jiraProjectQuery}
					variables={{ projectId: this.state.projectId }}
					// eslint-disable-next-line react/jsx-no-bind
					render={({ props }) => {
						const { isAccountAdmin } = this.props;
						const jiraProject = props?.viewer?.jiraProject;

						const filteredOptions = (jiraProject?.jiraIssueTypes || []).filter(project => project !== null);

						const options = filteredOptions.map(issueType => ({
							label: issueType.name || "",
							value: issueType.externalId || "",
						}));

						const currentIssueType = filteredOptions.find(o => o.externalId === this.state.issueType);

						let label = "";

						if (currentIssueType) {
							label = currentIssueType.name || "";
						}

						return (
							<>
								<div className={styles.label}>Issue type:</div>
								<Selector
									isSearchable
									placeholder="Select issue type"
									isDisabled={
										!Boolean(this.state.projectId) || (!!this.state.projectId && !Boolean(jiraProject?.jiraIssueTypes))
									}
									value={
										this.state.issueType
											? {
													value: this.state.issueType,
													label: label,
												}
											: undefined
									}
									options={options}
									onChange={this.onIssueTypeChange}
								/>
								<div className={cx(styles.label, styles.settingLabelAlignTop)}>Settings:</div>
								<div className={styles.settings}>
									<div className={styles.labels}>
										<div
											className={cx(styles.connectionStatusLabel, { [styles.connected]: jiraProject?.twoWaySync })}
											{...setTooltip(
												"Sync title, assignee, and due date with Jira",
												"bottom center",
												true,
												true,
												"140px",
											)}
										>
											2-way sync: {jiraProject?.twoWaySync ? "On" : "Off"}
										</div>
										<div
											className={cx(styles.connectionStatusLabel, { [styles.connected]: jiraProject?.pushStatus })}
											{...setTooltip("Push Fellow's action item status to Jira", "bottom center", true, true, "140px")}
										>
											Push status: {jiraProject?.pushStatus ? "On" : "Off"}
										</div>
									</div>
									<div className={styles.settingsHelpText}>
										{jiraProject == undefined && "To view settings, select a project first."}
										{jiraProject != undefined &&
											isAccountAdmin &&
											"To modify these settings, go to Workspace integrations."}
										{jiraProject != undefined &&
											!isAccountAdmin &&
											"Contact a workspace admin to modify these settings."}
									</div>
								</div>
							</>
						);
					}}
				/>
			</div>
		);
	};

	linkIssue = () => {
		const { node, editorState, selection, dispatch, cancel } = this.props;
		if (this.state.selectedIssue == null) return;

		const { participants, jira } = getContext(editorState);

		const {
			selectedIssue: {
				externalId,
				key,
				project,
				summary,
				dueDate,
				status: {
					name: statusName,
					statusCategory: { colorName: statusColor },
				},
				assignee,
			},
		} = this.state;

		const linkedIssueJSON = {
			jiraConnectionID: jira.id,
			provider: "JIRA",
			id: externalId,
			key: key,
			project: project.id,
			statusName: statusName,
			statusColor: statusColor,
		};

		// if we should create a new line instead of using the one the user is currently in.
		const createNewLine = !!this.props.initialText;

		const addAssigneeToAttrs = (attrs: typeof node.attrs) => {
			if (assignee && participants.find((a: AttendeeAttrs) => (a.id || a.user?.id) === assignee.id)) {
				return { ...attrs, assignees: [{ id: assignee.id, status: nodeAttrsToStatus(node.attrs) }] };
			}
			return attrs;
		};

		if (createNewLine) {
			// We're creating the action item on a new line
			const { $from, $to } = selection;
			const indent = node.attrs.indent;

			let attributes: MutableProseNodeAttrs = addAssigneeToAttrs({
				linkedIssue: linkedIssueJSON,
				blockId: uuid4(),
				dueDate,
			});

			if (indent !== undefined) {
				attributes["indent"] = indent;
			}

			const newNode = editorState.schema.nodes["todo"].createAndFill(attributes, editorState.schema.text(summary));
			if (!newNode) return;

			const tr = editorState.tr;
			tr.insert($to.pos, newNode);
			tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map($from.pos + 1) + 2));
			dispatch(tr);
			cancel();
		} else if (node.type.name !== "todo") {
			// we're changing the current line into a to-do
			const attrs: typeof node.attrs = addAssigneeToAttrs({
				linkedIssue: linkedIssueJSON,
				dueDate,
			});

			setBlockType(
				editorState.schema.nodes["todo"],
				attrs,
				null,
			)(editorState, (tr: Transaction) => {
				const mapping = tr.mapping;
				const startM = mapping.map(selection.$from.pos, 1);
				const endM = mapping.map(selection.$to.pos, 1);
				const modifiedTr = tr.replaceWith(startM + 1, endM, editorState.schema.text(summary));
				dispatch(modifiedTr);
				cancel();
			});
		} else {
			const attrs: typeof node.attrs = addAssigneeToAttrs({
				...node.attrs,
				linkedIssue: linkedIssueJSON,
				dueDate,
			});

			// we're editing the text of an empty to-do into the action item from jira
			let tr = editorState.tr.setNodeMarkup(selection.$from.pos, node.type, attrs);

			tr.replaceRangeWith(selection.$from.pos + 1, selection.$to.pos - 1, editorState.schema.text(summary));

			dispatch(tr);
			cancel();
		}
	};

	handleResultSelect = (
		e: React.MouseEvent<HTMLDivElement>,
		{ result }: { result: { title: string; key: string; data: JiraIssueSearchResultType } },
	) => {
		this.setState(
			{ selectedIssueSearchResult: result.data, search: `(${result.data.key}) ${result.data.summary}` },
			() => {
				fetchQuery<GetIssueQuery>(environment, getIssueQuery, { id: result.data.id })
					.toPromise()
					.then(value => {
						if (!value) return;
						this.setState({
							selectedIssue: value.viewer.jiraIssue,
							selectedIssueSearchResult: result.data,
							search: `(${result.data.key}) ${result.data.summary}`,
						});
					});
			},
		);
	};

	handleSearchChange = (e: React.MouseEvent<HTMLElement>, v: Overwrite<SearchResultProps, { value?: string }>) => {
		const { value } = v;
		if (value !== undefined) {
			if (this.state.selectedIssue) {
				this.setState({
					search: value.replace(/^\(.+?\)/, ""),
					selectedIssue: undefined,
					selectedIssueSearchResult: undefined,
				});
			} else {
				this.setState({ search: value });
			}
		}
	};

	handleSearchChangeDebounced = debounce((value: string) => {
		this.setState({ searchDebounced: value });
	}, 500);

	componentDidUpdate(prevProps: Readonly<IntegrationLinkModalProps>, prevState: Readonly<IntegrationLinkModalState>) {
		if (prevState.search !== this.state.search && !this.state.selectedIssue) {
			this.handleSearchChangeDebounced(this.state.search);
		}
	}

	renderFindExistingIssue = () => {
		const { isAccountAdmin, editorState } = this.props;
		const { selectedIssue } = this.state;

		const {
			jira: { baseUrl },
		} = getContext(editorState);

		return (
			<div className={styles.grid}>
				<div className={styles.label}>Jira issue:</div>
				<QueryRenderer<FindExistingIssueQuery>
					environment={environment}
					query={findExistingIssueQuery}
					variables={{ query: this.state.searchDebounced }}
					// eslint-disable-next-line react/jsx-no-bind
					render={({ props }) => {
						return (
							<Search
								autoFocus
								icon={<i aria-hidden="true" className="dropdown icon" />}
								className={styles.search}
								loading={
									(!props || (this.state.search !== this.state.searchDebounced && !this.state.selectedIssue)) &&
									this.state.search !== ""
								}
								onResultSelect={this.handleResultSelect}
								onSearchChange={this.handleSearchChange}
								results={props?.viewer?.jiraSearch?.map(jiraIssue => ({
									title: `(${jiraIssue?.key}) ${jiraIssue?.summary}`,
									key: jiraIssue?.key,
									data: jiraIssue,
								}))}
								// eslint-disable-next-line react/jsx-no-bind
								resultRenderer={result => (
									<div className={styles.searchResultContainer}>
										<img className={styles.searchResultImage} src={`${baseUrl}${result.data.img}`} />
										<span className={styles.searchResultText}>{result.title}</span>
									</div>
								)}
								value={this.state.search}
								placeholder="Search for a ticket number or title"
							/>
						);
					}}
				/>
				<div className={cx(styles.label, styles.settingLabelAlignTop)}>Settings:</div>
				<div className={styles.settings}>
					<div className={styles.labels}>
						<div
							className={cx(styles.connectionStatusLabel, { [styles.connected]: selectedIssue?.project?.twoWaySync })}
							{...setTooltip("Sync title, assignee, and due date with Jira", "bottom center", true, true, "140px")}
						>
							2-way sync: {selectedIssue?.project?.twoWaySync ? "On" : "Off"}
						</div>
						<div
							className={cx(styles.connectionStatusLabel, { [styles.connected]: selectedIssue?.project?.pushStatus })}
							{...setTooltip("Push Fellow's action item status to Jira", "bottom center", true, true, "140px")}
						>
							Push status: {selectedIssue?.project?.pushStatus ? "On" : "Off"}
						</div>
					</div>
					{isAccountAdmin ? (
						<div className={styles.settingsHelpText}>To modify these settings, go to Workspace integrations.</div>
					) : (
						<div className={styles.settingsHelpText}>Contact a workspace admin to modify these settings.</div>
					)}
				</div>
			</div>
		);
	};

	render() {
		const { projectId, issueType, title, selectedTab, loading } = this.state;
		return (
			<Modal onClose={this.props.cancel}>
				<Modal.Header>
					<Modal.Title>
						<JiraLogo /> Jira
					</Modal.Title>
					<Modal.HeaderActions onClose={this.props.cancel} />
					<nav className={cx(styles.tabBar)}>
						<a
							key="create"
							href="#"
							className={cx(styles.tab, { [styles.tabActive]: selectedTab === "create" })}
							onClick={this.onClickTab("create")}
						>
							Create new issue
						</a>
						<a
							key="link"
							href="#"
							className={cx(styles.tab, { [styles.tabActive]: selectedTab === "link" })}
							onClick={this.onClickTab("link")}
						>
							Import existing issue
						</a>
					</nav>
				</Modal.Header>
				<Modal.Body dark className={styles.body}>
					{selectedTab === "create" && this.renderCreateNewIssue()}
					{selectedTab === "link" && this.renderFindExistingIssue()}
				</Modal.Body>
				<Modal.Footer>
					<Button size="normal" variant="secondary" onClick={this.props.cancel}>
						Cancel
					</Button>
					{selectedTab == "create" && (
						<Button
							size="normal"
							variant="primary"
							onClick={this.createIssue}
							disabled={!(projectId && issueType && title) || loading}
						>
							Create issue
						</Button>
					)}
					{selectedTab === "link" && (
						<Button
							size="normal"
							onClick={this.linkIssue}
							variant="primary"
							disabled={this.state.selectedIssue == null || this.state.selectedIssueSearchResult == null}
						>
							Import issue
						</Button>
					)}
				</Modal.Footer>
			</Modal>
		);
	}
}

export class IntegrationLink {
	private targetDomNode: HTMLElement;
	private readonly root: HTMLDivElement;
	private mounted: boolean;

	constructor({ editorState, dispatch, selection, initialText, isAccountAdmin }: JiraIntegrationLinkProps) {
		this.root = document.createElement("div");
		this.targetDomNode = document.body;

		this.targetDomNode.appendChild(this.root);

		ReactDOM.render(
			<IntegrationLinkModal
				dispatch={dispatch}
				cancel={this.abortLinking}
				node={selection.node}
				editorState={editorState}
				selection={selection}
				initialText={initialText}
				isAccountAdmin={isAccountAdmin}
			/>,
			this.root,
		);
		this.mounted = true;
	}

	abortLinking = () => {
		if (!this.mounted) return;
		this.mounted = false;
		ReactDOM.unmountComponentAtNode(this.root);
		this.targetDomNode.removeChild(this.root);
	};
}
